'use client';

import {useRouter} from 'next/navigation';
import {useTransition} from 'react';
import {useTranslation} from 'react-i18next';
import {trackNewsCategoryFilter, trackNewsListingCardClick} from '../../analytics';
import {NEWS_EMPTY} from '../../content';
import type {NewsCardView} from '../../model';
import type {ListingCardProps} from '../listing';
import {ListingPage, type ListingPageFilter} from '../listing/ListingPage';
import {newsCardProps} from '../listing/newsCard';
import {Chip} from '../primitives';
import type {CardListingFilterConfig, CardListingServerPaging} from './listing-card-item';

export interface NewsCardGridListingProps {
  locale: string;
  items: NewsCardView[];
  filter?: CardListingFilterConfig;
  paging?: CardListingServerPaging;
  project?: string;
}

function buildListingHref({project, category, page}: {project?: string; category?: string; page?: number}): string {
  const params = new URLSearchParams();
  if (project) {
    params.set('project', project);
  }
  if (category) {
    params.set('category', category);
  }
  if (page && page > 1) {
    params.set('page', String(page));
  }
  const query = params.toString();
  return query ? `?${query}` : '?';
}

export function NewsCardGridListing({locale, items, filter, paging, project}: NewsCardGridListingProps) {
  const {t} = useTranslation();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const navigate = (href: string) => {
    startTransition(() => {
      router.push(href);
    });
  };

  // Resolve a category id to its visible label for the filter analytics (the
  // "All" chip has no id → falls back to the all-news label).
  const categoryName = (id?: string) => (id ? (filter?.items.find(option => option.id === id)?.label ?? id) : t('all_news'));

  const cards: ListingCardProps[] = items.map(article => {
    const card = newsCardProps(article, locale);
    const tracked = card.id ? {id: card.id, name: card.title} : undefined;
    return {
      id: card.id,
      title: card.title,
      image: card.image,
      href: card.href,
      primaryMeta: card.primaryMeta,
      badges: card.badges,
      tags: article.tag ? (
        <Chip color="dark" size="medium">
          {article.tag}
        </Chip>
      ) : undefined,
      onClick: tracked
        ? () => {
            trackNewsListingCardClick(tracked);
          }
        : undefined,
    };
  });

  const filterConfig: ListingPageFilter | undefined = filter && {
    items: filter.items,
    activeId: filter.activeId,
    allLabel: t('all_news'),
    onSelect: (id?: string) => {
      trackNewsCategoryFilter(categoryName(id), categoryName(filter.activeId));
      navigate(buildListingHref({project, category: id}));
    },
  };

  const pagination =
    paging && paging.totalPages > 1
      ? {
          page: paging.page,
          totalPages: paging.totalPages,
          onChange: (nextPage: number) => {
            navigate(buildListingHref({project, category: filter?.activeId, page: nextPage}));
          },
        }
      : undefined;

  return <ListingPage title={t('news')} cards={cards} empty={NEWS_EMPTY} filter={filterConfig} pagination={pagination} busy={isPending} />;
}

NewsCardGridListing.displayName = 'NewsCardGridListing';
